/** ⌘K search (FR-ADM search): jump to a case, session, feedback item or system by ID or text. */
import type { SearchResult } from '@uatp/shared';
import { Dialog, Spinner, cn } from '@uatp/ui';
import { useQuery } from '@tanstack/react-query';
import { CalendarCheck, CornerDownLeft, FileText, MessageSquareWarning, Plus, Search, Server } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { get } from '../api/client';
import { useAuth } from '../auth/auth';
import { useShell } from './store';

const KIND_ICON = { case: FileText, session: CalendarCheck, feedback: MessageSquareWarning, system: Server } as const;
const KIND_LABEL = { case: 'Test case', session: 'Session', feedback: 'Feedback', system: 'System' } as const;

interface Item {
  id: string;
  label: string;
  sub?: string;
  code?: string;
  href: string;
  icon: typeof Search;
  group: string;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function CommandPalette() {
  const { paletteOpen: open, setPaletteOpen } = useShell();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const term = useDebounced(q.trim(), 200);

  const results = useQuery({
    queryKey: ['/search', term],
    queryFn: ({ signal }) => get<SearchResult[]>(`/search?q=${encodeURIComponent(term)}`, signal),
    enabled: open && term.length >= 2,
    staleTime: 10_000,
  });

  const items = useMemo<Item[]>(() => {
    if (term.length < 2) {
      const quick: Item[] = [
        { id: 'go-sessions', label: 'UAT sessions', href: '/sessions', icon: CalendarCheck, group: 'Go to' },
        { id: 'go-library', label: 'Test library', href: '/library', icon: FileText, group: 'Go to' },
        { id: 'go-systems', label: 'Systems', href: '/systems', icon: Server, group: 'Go to' },
      ];
      if (can('sessions', 'create')) quick.push({ id: 'new-session', label: 'New session', href: '/sessions/new', icon: Plus, group: 'Create' });
      if (can('testCases', 'create')) quick.push({ id: 'new-case', label: 'New test case', href: '/library/new', icon: Plus, group: 'Create' });
      return quick;
    }
    return (results.data ?? []).map((r) => ({
      id: `${r.kind}:${r.id}`,
      label: r.title,
      sub: r.subtitle,
      code: r.code,
      href: r.href,
      icon: KIND_ICON[r.kind],
      group: KIND_LABEL[r.kind],
    }));
  }, [term, results.data, can]);

  useEffect(() => setActive(0), [items]);
  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const go = (item: Item | undefined) => {
    if (!item) return;
    setPaletteOpen(false);
    navigate(item.href);
  };

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  let lastGroup = '';
  return (
    <Dialog
      open={open}
      onOpenChange={setPaletteOpen}
      title={<span className="sr-only">Search</span>}
      hideClose
      size="lg"
      width={620}
      className="mt-[8vh] self-start [&>div:first-child]:hidden"
      bodyClassName="p-0"
      initialFocus="first"
    >
      <div className="flex items-center gap-2 border-b border-border px-4">
        {results.isFetching ? <Spinner size={16} /> : <Search className="size-4 text-text-3" aria-hidden />}
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, items.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              go(items[active]);
            }
          }}
          placeholder="Search by ID (TC-0012, SES-2041, FB-0181) or text…"
          aria-label="Search"
          role="combobox"
          aria-expanded
          aria-controls="palette-list"
          aria-activedescendant={items[active] ? `palette-${items[active].id}` : undefined}
          className="h-12 flex-1 border-none bg-transparent text-15 text-text outline-none placeholder:text-text-3 focus-visible:shadow-none"
        />
      </div>
      <div ref={listRef} id="palette-list" role="listbox" aria-label="Results" className="max-h-[50vh] overflow-auto p-1.5">
        {term.length >= 2 && !results.isFetching && items.length === 0 && (
          <p className="px-3 py-8 text-center text-13 text-text-2">No matches for “{term}”.</p>
        )}
        {items.map((item, i) => {
          const header = item.group !== lastGroup ? item.group : null;
          lastGroup = item.group;
          const Icon = item.icon;
          return (
            <div key={item.id}>
              {header && <div className="px-3 pb-1 pt-2.5 text-11 font-semibold uppercase tracking-wide text-text-3">{header}</div>}
              {/* Options are driven from the input (aria-activedescendant); the pointer is a convenience. */}
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */}
              <div
                id={`palette-${item.id}`}
                role="option"
                aria-selected={i === active}
                data-index={i}
                onMouseMove={() => setActive(i)}
                onClick={() => go(item)}
                className={cn('flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-13', i === active && 'bg-primary-soft text-primary-text')}
              >
                <Icon className="size-4 shrink-0 opacity-70" aria-hidden />
                {item.code && <span className="shrink-0 font-mono text-12 text-text-2">{item.code}</span>}
                <span className="min-w-0 flex-1 truncate font-medium">{item.label}</span>
                {item.sub && <span className="hidden max-w-[40%] truncate text-12 text-text-3 sm:inline">{item.sub}</span>}
                {i === active && <CornerDownLeft className="size-3.5 shrink-0 opacity-60" aria-hidden />}
              </div>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}
